<template>
  <div class="content">
    <div class="user-management">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <Card>
              <template #header>
                <div class="header-actions">
                  <h5 class="title">Uploaded Files</h5>
                  <div class="actions-right">
                    <button :disabled="loadingStates.upload" class="add-employee" @click="startUpload()">
                      <span v-if="loadingStates.upload">
                        <img src="/static/img/loader.gif" alt="Loading" class="loader-icon" />
                      </span>
                      <span v-else>Upload File</span>
                    </button>

                    <input type="file" ref="fileInput" accept=".docx,.pdf" class="d-none" @change="handleFileUpload" />
                  </div>
                </div>
              </template>

              <div>
                <CustomTable :loading="loadingStates.table" :columns="tableColumns" :data="fileList">
                  <template v-slot:fileName="{ item }">
                    <span class="truncate-text" :title="item.loaderConfig.metadata.fileName || '-'">
                      {{ item.loaderConfig.metadata.fileName || '-' }}
                    </span>
                  </template>

                  <template v-slot:status="{ item }">
                    <span :title="item.status">{{ item.status || '-' }}</span>
                  </template>

                  <template v-slot:username="{ item }">
                    {{ item.loaderConfig.metadata.username || '-' }}
                  </template>

                  <template v-slot:uploadedAt="{ item }">
                    <span>{{ formatDate(item.files[0].uploaded) }}</span>
                  </template>

                  <template v-slot:action="{ item }">
                    <div class="action-buttons">
                      <button class="edit-btn btn-status" :title="'Edit'" @click="startUpload(item, true)">
                        <span v-if="loadingStates.edit">
                          <img src="/static/img/loader.gif" alt="Loading" class="loader-icon" />
                        </span>
                        <PencilIcon v-else color="gray" :width="16" :height="16" />
                      </button>

                      <button class="delete-btn btn-status" title="Delete" @click="openDeleteModal(item)">
                        <i class="fa fa-trash" style="color: gray;"></i>
                      </button>
                    </div>
                  </template>
                </CustomTable>
              </div>
            </Card>

            <ReuseableModal :modelValue="isDeleteModalOpen" @update:modelValue="isDeleteModalOpen = $event"
              title="Confirm Deletion" submitButtonText="Delete" :loading="loadingStates.delete"
              @submit="confirmDelete">
              <div>Are you sure you want to delete this file?
              </div>
            </ReuseableModal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import Card from "src/components/UIComponents/Cards/Card.vue";
import PencilIcon from 'src/assets/icons/PencilIcon.js';
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import { isDocx, formatToLocaleString } from "@/utils/fileUtils";
import { buildFormData } from "@/utils/formDataBuilder";
import { uploadFile, fetchDocumentsAPI, deleteLoader } from "@/utils/flowiseService";
import { createSearchIndexIfNeeded, deleteByFileId } from "@/utils/backendService";
import { isPdf, validateFileSize } from "../../../../utils/fileUtils";
import { canUploadFile } from "../../../../utils/flowiseService";

const FLOWISE_BASE = process.env.VUE_APP_PROD_FLOWISE_BASE;
const BACKEND_BASE = process.env.SERVER_URL;

export default {
  components: {
    Card,
    CustomTable, 
    PencilIcon,
    ReuseableModal
  },

  data() {
    return {
      searchTerm: "",
      tableColumns: [
        { label: "File Name", key: "fileName", sortable: true, width: 40 },
        { label: "Uploaded By", key: "username", sortable: true, width: 20 },
        { label: "Uploaded At", key: "uploadedAt", sortable: true, width: 20 },
        { label: "Action", key: "action", width: 20 }
      ],
      isDeleteModalOpen: false,
      selectedFile: null,
      editTarget: null,
      isEdit: false,

      loading: false,
      newFileUploadLoading: false,
      deleteLoading: false,
      response: null,
      fileList: [],

      loadingStates: {
        table: false,
        upload: false,
        edit: false,
        delete: false
      },

      documentStoreId: process.env.VUE_APP_PROD_FLOWISE_DOCUMENT_STORE_ID,
      apiKey: process.env.VUE_APP_PROD_FLOWISE_API_KEY,
      dbName: process.env.VUE_APP_PROD_DATABASE,
      collectionName: process.env.VUE_APP_PROD_COLLECTION,
      OPENAI_EMBEDDINGS_KEY: process.env.VUE_APP_PROD_OPENAI_EMBEDDINGS_KEY,
      MONGODB_Atlas_KEY: process.env.VUE_APP_PROD_MONGODB_ATLAS_KEY,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user || { username: 'anonymous' };
    }
  },

  mounted() {
    this.fetchDocuments();
  },

  methods: {
    formatDate(dateStr) {
      return formatToLocaleString(dateStr);
    },

    triggerFileInput(item, isEdit = false) {
      this.editTarget = item;
      this.isEdit = isEdit;
      this.$refs.editFileInput.click();
    },

    startUpload(item = null, edit = false) {
      this.editTarget = item;
      this.isEdit = edit === true;
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return

      if (!isDocx(file) && !isPdf(file)) {
        swal("Only DOCX or PDF allowed", "Please upload another file", "error");
        return;
      }

      const sizeCheck = await validateFileSize(file, 2);
      if (!sizeCheck.ok) {
        swal("Invalid file size", sizeCheck.message, "error");
        return;
      }

      const fileSizeMB = file.size / (1024 * 1024);
      const check = await canUploadFile(fileSizeMB, FLOWISE_BASE, this.apiKey);

      if (!check.ok) {
        swal("Limit reached", check.message, "error");
        return;
      }

      const filename = file.name.replace(/\.[^/.]+$/, "");
      const uniqueFileId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const formData = buildFormData({
        file,
        filename,
        uniqueFileId,
        user: this.user,
        OPENAI_EMBEDDINGS_KEY: this.OPENAI_EMBEDDINGS_KEY,
        MONGODB_Atlas_KEY: this.MONGODB_Atlas_KEY,
        dbName: this.dbName,
        collectionName: this.collectionName
      });

      try {
        if (this.isEdit && this.editTarget) {
          this.loadingStates.edit = true;
        } else {
          this.loadingStates.upload = true;
        }

        const loaderId =
          this.editTarget && this.editTarget.id
            ? this.editTarget.id
            : null;

        const fileId =
          this.editTarget &&
            this.editTarget.loaderConfig &&
            this.editTarget.loaderConfig.metadata &&
            this.editTarget.loaderConfig.metadata.fileId
            ? this.editTarget.loaderConfig.metadata.fileId
            : null;

        const uploadResult = await uploadFile({
          FLOWISE_BASE,
          documentStoreId: this.documentStoreId,
          apiKey: this.apiKey,
          formData
        });

        if (uploadResult && uploadResult.ok && (!loaderId && !fileId)) {
          await createSearchIndexIfNeeded({
            BACKEND_BASE,
            dbName: this.dbName,
            collectionName: this.collectionName
          });
          swal("File has been uploaded", "Uploaded", "success");
        }

        if (this.isEdit && this.editTarget) {
          await this.deleteFile(this.editTarget.id, this.editTarget.loaderConfig.metadata.fileId, true);
          swal("File has been updated", "Updated", "success");
        }

      } catch (err) {
        console.error('Upload error', err);
        swal('Upload failed', err.message || 'Something went wrong', 'error');
      } finally {
        this.loadingStates.upload = false;
        this.loadingStates.edit = false;
        this.isEdit = false;
        this.editTarget = null;
        await this.fetchDocuments();
      }
    },

    async fetchDocuments() {
      this.loadingStates.table = true;
      try {
        const data = await fetchDocumentsAPI({
          FLOWISE_BASE,
          documentStoreId: this.documentStoreId,
          apiKey: this.apiKey
        });

        this.fileList = data.loaders || [];
      } catch (err) {
        console.error('Error fetching files:', err);
        swal('Fetch failed', 'Could not load files', 'error');
      } finally {
        this.loadingStates.table = false;
      }
    },

    openDeleteModal: function (item) {
      this.selectedFile = item;
      this.isDeleteModalOpen = true;
    },

    async confirmDelete() {
      if (!this.selectedFile) return;
      const loaderId = this.selectedFile.id;
      const fileId = this.selectedFile.loaderConfig.metadata.fileId;
      await this.deleteFile(loaderId, fileId, false);
    },

    async deleteFile(loaderId, fileId = null, replace = false) {
      if (!loaderId || !fileId) {
        console.warn('deleteFile called without loaderId or fileId', { loaderId, fileId });
        return;
      }

      this.loadingStates.delete = true;
      try {
        const flowiseJson = await deleteLoader({
          FLOWISE_BASE,
          documentStoreId: this.documentStoreId,
          loaderId,
          apiKey: this.apiKey
        });

        const backendJson = await deleteByFileId({
          BACKEND_BASE,
          dbName: this.dbName,
          collectionName: this.collectionName,
          fileId
        });

        if (!replace) {
          this.isDeleteModalOpen = false;
          swal('File has been deleted', 'Deleted', 'success');
        }

        await this.fetchDocuments();
        return { flowise: flowiseJson, backend: backendJson };
      } catch (err) {
        console.error('Error deleting file:', err);
        swal('Delete failed', err.message || 'Could not delete file', 'error');
      } finally {
        this.loadingStates.delete = false;
      }
    }
  }
};
</script>

<style scoped>
.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.actions-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.loader-icon {
  height: 18px;
  vertical-align: middle;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.icon-btn {
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
}

.add-employee[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.d-none {
  display: none;
}

.truncate-text {
  display: inline-block;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/ChatbotManagement/ChatbotManagement.vue